// src/hooks/usePanelRiskSummary.ts
import { QueryClient, useQuery, useQueryClient } from "@tanstack/react-query";

import { getPanelRiskSummary } from "../../api/geneCodes/PanelApi";
import { PanelRiskRow } from "../../types/geneCodes/Panel";

export const PANEL_RISK_SUMMARY_QUERY_KEY = "panel-risk-summary";

// Module level so `data?.rows ?? EMPTY` keeps the same reference between renders.
const EMPTY: PanelRiskRow[] = [];

let fallbackClient: QueryClient | null = null;

// WheelModal's suite mounts this tree with no QueryClientProvider, and v5's useQueryClient throws there — degrade to an idle client rather than take the whole modal down.
const useResolvedClient = (): [QueryClient, boolean] => {
	try {
		return [useQueryClient(), true];
	} catch {
		if (!fallbackClient) fallbackClient = new QueryClient();
		return [fallbackClient, false];
	}
};

export interface UsePanelRiskSummaryResult {
	rows: PanelRiskRow[];
	isLoading: boolean;
	isError: boolean;
}

export const usePanelRiskSummary = (sampleId?: string): UsePanelRiskSummaryResult => {
	const [client, hasProvider] = useResolvedClient();

	const query = useQuery(
		{
			queryKey: [PANEL_RISK_SUMMARY_QUERY_KEY, sampleId],
			queryFn: () => getPanelRiskSummary(sampleId as string),
			enabled: hasProvider && Boolean(sampleId),
		},
		client
	);

	return {
		rows: query.data?.rows ?? EMPTY,
		isLoading: query.isLoading,
		isError: query.isError,
	};
};
